import * as React from "react";

import type {
  Activity,
  ActivityContentCommentAdded,
  ActivityContentGoalClosing,
  ActivityContentGoalDiscussionCreation,
  ActivityContentGoalReopening,
  ActivityContentGoalTimeframeEditing,
  ActivityContentProjectDiscussionSubmitted,
} from "@/api";
import type { ActivityHandler, FeedItemProps } from "../interfaces";

import * as Activities from "@/models/activities";
import { match } from "ts-pattern";
import { Link, Summary } from "turboui";
import { Trans } from "../i18n";
import i18n from "@/i18n";
import { activityAuthorName, commentPath, commentedLink, goalLink, projectLink } from "../feedItemLinks";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { parseCommentContent } from "@/models/comments";

const CommentAdded: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity): string {
    const { activity: commentedActivity, comment } = content(activity);

    if (!commentedActivity) {
      return paths.homePath();
    }

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_closing", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_discussion_creation", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_reopening", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("project_discussion_submitted", () => {
        const projectSubmitted = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        return commentPath(paths.projectDiscussionPath(projectSubmitted.discussion!.id!), comment);
      })
      .with("project_resuming", () => {
        return commentPath(paths.projectActivityPath(commentedActivity.id), comment);
      })
      .with("project_pausing", () => {
        return commentPath(paths.projectActivityPath(commentedActivity.id), comment);
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },

  PageTitle(_props: { activity: any }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page, paths }: FeedItemProps) {
    const { activity: commentedActivity, comment } = content(activity);
    const author = activityAuthorName(activity);

    if (!commentedActivity) {
      return <Trans i18nKey="{{author}} commented" values={{ author }} />;
    }

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => {
        const c = commentedActivity.content as ActivityContentGoalTimeframeEditing;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          activity: <Link to={path}>{null}</Link>,
          goal: goalLink(paths, goal),
        };
        const values = { author, goalName: goal.name };

        if (page === "goal") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>timeframe change</activity>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>timeframe change</activity> in the <goal>{{goalName}}</goal> goal"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("goal_closing", () => {
        const c = commentedActivity.content as ActivityContentGoalClosing;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          activity: <Link to={path}>{null}</Link>,
          goal: goalLink(paths, goal),
        };
        const values = { author, goalName: goal.name };

        if (page === "goal") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>goal closing</activity>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>goal closing</activity> in the <goal>{{goalName}}</goal> goal"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("goal_discussion_creation", () => {
        const { goal } = commentedActivity.content as ActivityContentGoalDiscussionCreation;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        let activityLink = <React.Fragment />;

        if (commentedActivity.commentThread) {
          activityLink = <Link to={path}>{commentedActivity.commentThread.title}</Link>;
        }
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          discussion: activityLink,
          goal: goalLink(paths, goal),
        };
        const values = {
          author,
          title: commentedActivity.commentThread?.title ?? i18n.t("a discussion"),
          goalName: goal.name,
        };

        if (page === "goal") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <discussion>{{title}}</discussion>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <discussion>{{title}}</discussion> in the <goal>{{goalName}}</goal> goal"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("goal_reopening", () => {
        const c = commentedActivity.content as ActivityContentGoalReopening;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          activity: <Link to={path}>{null}</Link>,
          goal: goalLink(paths, goal),
        };
        const values = { author, goalName: goal.name };

        if (page === "goal") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>goal reopening</activity>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on the <activity>goal reopening</activity> in the <goal>{{goalName}}</goal> goal"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("project_discussion_submitted", () => {
        const { discussion, title, project } = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        const path = discussion?.id ? paths.projectDiscussionPath(discussion.id) : null;
        const action = path ? commentedLink(path, comment) : "commented";
        let activityLink = <React.Fragment />;

        if (path && (title || discussion?.title)) {
          activityLink = <Link to={path}>{title || discussion?.title}</Link>;
        }
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          discussion: activityLink,
          project: projectLink(paths, project),
        };
        const values = {
          author,
          title: path && (title || discussion?.title) ? title || discussion?.title : i18n.t("a discussion"),
          projectName: project.name,
        };

        if (page === "project") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <discussion>{{title}}</discussion>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <discussion>{{title}}</discussion> in the <project>{{projectName}}</project> project"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("project_resuming", () => {
        const path = paths.projectActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const project = Activities.getProject(commentedActivity);
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          activity: <Link to={path}>{null}</Link>,
          project: projectLink(paths, project),
        };
        const values = { author, projectName: project.name };

        if (page === "project") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <activity>project resuming</activity>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <activity>project resuming</activity> in the <project>{{projectName}}</project> project"
              values={values}
              components={components}
            />
          );
        }
      })
      .with("project_pausing", () => {
        const path = paths.projectActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const project = Activities.getProject(commentedActivity);
        const components = {
          action: typeof action === "string" ? <React.Fragment /> : action,
          activity: <Link to={path}>{null}</Link>,
          project: projectLink(paths, project),
        };
        const values = { author, projectName: project.name };

        if (page === "project") {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <activity>project pausing</activity>"
              values={values}
              components={components}
            />
          );
        } else {
          return (
            <Trans
              i18nKey="{{author}} <action>commented</action> on <activity>project pausing</activity> in the <project>{{projectName}}</project> project"
              values={values}
              components={components}
            />
          );
        }
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },

  FeedItemContent({ activity }: { activity: Activity }) {
    const { comment } = content(activity);
    const { mentionedPersonLookup } = useRichEditorHandlers();
    const commentContent = parseCommentContent(comment?.content);

    if (!commentContent) {
      return null;
    }

    return <Summary content={commentContent} characterCount={200} mentionedPersonLookup={mentionedPersonLookup} />;
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-start";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    const commentedActivity = content(activity).activity!;
    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => i18n.t("Re: timeframe change"))
      .with("goal_closing", () => i18n.t("Re: goal closing"))
      .with("goal_discussion_creation", () =>
        i18n.t("Re: {{title}}", { title: commentedActivity.commentThread?.title }),
      )
      .with("goal_reopening", () => i18n.t("Re: goal reopening"))
      .with("project_discussion_submitted", () =>
        i18n.t("Re: {{title}}", { title: commentedActivity.commentThread?.title }),
      )
      .with("project_resuming", () => i18n.t("Re: project resuming"))
      .with("project_pausing", () => i18n.t("Re: project pausing"))
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    const commentedActivity = content(activity).activity!;

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => {
        const c = commentedActivity.content as ActivityContentGoalTimeframeEditing;
        return c.goal!.name!;
      })
      .with("goal_closing", () => {
        const c = commentedActivity.content as ActivityContentGoalClosing;
        return c.goal!.name!;
      })
      .with("goal_discussion_creation", () => {
        const c = commentedActivity.content as ActivityContentGoalDiscussionCreation;
        return c.goal!.name!;
      })
      .with("goal_reopening", () => {
        const c = commentedActivity.content as ActivityContentGoalReopening;
        return c.goal!.name!;
      })
      .with("project_discussion_submitted", () => {
        const c = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        return c.title!;
      })
      .with("project_resuming", () => {
        const project = Activities.getProject(commentedActivity);
        return project.name!;
      })
      .with("project_pausing", () => {
        const project = Activities.getProject(commentedActivity);
        return project.name!;
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },
};

function content(activity: Activity): ActivityContentCommentAdded {
  return activity.content as ActivityContentCommentAdded;
}

export default CommentAdded;
